import { ref, computed } from 'vue'
import { dictDataList } from '@/views/Dict/service' // 根据你的实际路径调整

// 字典项接口定义
export interface DictItem {
    label: string
    value: string | number
    [key: string]: any
}

// 字典缓存
const dictCache = new Map<string, DictItem[]>()

// 全局加载状态
const loadingStates = ref<Record<string, boolean>>({})

/**
 * 字典查询 composable
 */
export const useDict = () => {
    /**
     * 获取字典数据
     * @param type 字典类型
     * @param useCache 是否使用缓存，默认true
     */
    const getDictData = async (type: string, useCache: boolean = true): Promise<DictItem[]> => {
        // 如果使用缓存且缓存中存在，直接返回
        if (useCache && dictCache.has(type)) {
            return dictCache.get(type)!
        }

        // 设置加载状态
        loadingStates.value[type] = true

        try {
            const response = await dictDataList({ params: { dictType: type }, pageSize: 999, pageNum: 1 })
            const { list = [] } = response?.data || response || []

            const data = list.sort((a, b) => { return a.dictSort - b.dictSort }).map(item => ({
                label: item.dictLabel,
                value: item.dictValue,
                groupType: item.groupType,
                dictGroup: item.groupType && item.dictGroup ? item.dictGroup.map(i => { return { label: i.groupLable, value: i.groupValue, groupType: i.groupType } }) : []
            }))
            // 存入缓存
            if (useCache) {
                dictCache.set(type, data)
            }

            return data
        } catch (error) {
            console.error(`获取字典 ${type} 失败:`, error)
            return []
        } finally {
            loadingStates.value[type] = false
        }
    }

    /**
     * 获取字典的响应式数据
     * @param type 字典类型
     */
    const useDictData = (type: string) => {
        const data = ref<DictItem[]>([])
        const loading = computed(() => loadingStates.value[type] || false)

        const loadData = async (useCache: boolean = true) => {
            data.value = await getDictData(type, useCache)
        }

        // 立即加载数据
        loadData()

        return {
            data,
            loading,
            refresh: () => loadData(false) // 刷新时不使用缓存
        }
    }

    /**
     * 根据value获取label
     * @param type 字典类型
     * @param value 字典值
     */
    const getDictLabel = async (type: string, value: string | number): Promise<string> => {
        const dictData = await getDictData(type)
        const item = dictData.find(item => item.value === value)
        return item?.label || value.toString()
    }

    /**
     * 根据label获取value
     * @param type 字典类型
     * @param label 字典标签
     */
    const getDictValue = async (type: string, label: string): Promise<string | number | undefined> => {
        const dictData = await getDictData(type)
        const item = dictData.find(item => item.label === label)
        return item?.value
    }

    /**
     * 清除指定类型的字典缓存
     * @param type 字典类型，不传则清除全部
     */
    const clearDictCache = (type?: string) => {
        if (type) {
            dictCache.delete(type)
        } else {
            dictCache.clear()
        }
    }

    /**
     * 预加载字典数据
     * @param types 字典类型数组
     */
    const preloadDictData = async (types: string[]) => {
        const promises = types.map(type => getDictData(type))
        await Promise.all(promises)
    }

    return {
        getDictData,
        useDictData,
        getDictLabel,
        getDictValue,
        clearDictCache,
        preloadDictData
    }
}
